import { CylinderGeometry, Quaternion, Vector3 } from 'three/webgpu'
import { easing } from 'maath'

export default class ClawRod {
	static defaults = {
		radial: 16,
		rings: 48,
		stiffness: 0.1,
		trail: 0.05,
	}

	constructor(mesh, model) {
		this.options = ClawRod.defaults
		this.mesh = mesh
		this.model = model
		this.#build()
	}

	#build() {
		const { mesh } = this
		const { radial, rings } = this.options

		mesh.geometry.computeBoundingBox()

		const { min, max } = mesh.geometry.boundingBox
		const radius = max.x * mesh.scale.x

		this.bottom = min.y * mesh.scale.y
		this.length = (max.y - min.y) * mesh.scale.y

		mesh.geometry.dispose()
		mesh.geometry = new CylinderGeometry(radius, radius, this.length, radial, rings, true)
		mesh.scale.setScalar(1)
		mesh.frustumCulled = false

		this.positions = mesh.geometry.attributes.position
		this.normals = mesh.geometry.attributes.normal
		this.restPositions = this.positions.array.slice()
		this.restNormals = this.normals.array.slice()

		this.p0 = new Vector3()
		this.p1 = new Vector3()
		this.p2 = new Vector3()
		this.p3 = new Vector3()
		this.point = new Vector3()
		this.tangent = new Vector3()
		this.vertex = new Vector3()
		this.target = new Vector3()
		this.axis = new Vector3(0, 1, 0)
		this.bend = new Quaternion()

		mesh.updateWorldMatrix(true, false)
		this.top = this.#toModel(this.p3.set(0, this.bottom + this.length, 0)).clone()
		this.home = this.top.clone()
		this.offset = this.top.x - this.#toModel(this.p0.set(0, this.bottom, 0)).x
		this.update(0)
	}

	extend(factor) {
		this.length *= factor
		this.model.updateMatrixWorld(true)
		this.home.copy(this.#toModel(this.p3.set(0, this.bottom + this.length, 0)))
		this.top.copy(this.home)
	}

	#toModel(point) {
		return this.model.worldToLocal(this.mesh.localToWorld(point))
	}

	#toRod(point) {
		return this.mesh.worldToLocal(this.model.localToWorld(point))
	}

	#curve(delta) {
		const { p0, p1, p2, p3 } = this
		const base = this.#toModel(p0.set(0, this.bottom, 0))

		this.target.set(base.x + this.offset, this.home.y, this.home.z)
		easing.damp3(this.top, this.target, this.options.trail, delta)

		this.#toRod(p3.copy(this.top))
		this.#toRod(p2.copy(this.top).sub(this.axis)).sub(p3)
		p0.set(0, this.bottom, 0)

		const handle = p0.distanceTo(p3) * this.options.stiffness

		p1.set(0, this.bottom + handle, 0)
		p2.normalize().multiplyScalar(handle).add(p3)
	}

	#evaluate(t) {
		const { p0, p1, p2, p3 } = this
		const u = 1 - t

		this.point
			.copy(p0)
			.multiplyScalar(u * u * u)
			.addScaledVector(p1, 3 * u * u * t)
			.addScaledVector(p2, 3 * u * t * t)
			.addScaledVector(p3, t * t * t)

		this.tangent
			.copy(p0)
			.multiplyScalar(-3 * u * u)
			.addScaledVector(p1, 3 * u * u - 6 * u * t)
			.addScaledVector(p2, 6 * u * t - 3 * t * t)
			.addScaledVector(p3, 3 * t * t)
			.normalize()

		this.bend.setFromUnitVectors(this.axis, this.tangent)
	}

	update(delta) {
		const { radial, rings } = this.options
		const positions = this.positions.array
		const normals = this.normals.array

		this.#curve(delta)

		for (let ring = 0; ring <= rings; ring++) {
			this.#evaluate(1 - ring / rings)

			for (let side = 0; side <= radial; side++) {
				const index = (ring * (radial + 1) + side) * 3

				this.vertex.set(this.restPositions[index], 0, this.restPositions[index + 2]).applyQuaternion(this.bend)
				positions[index] = this.point.x + this.vertex.x
				positions[index + 1] = this.point.y + this.vertex.y
				positions[index + 2] = this.point.z + this.vertex.z

				this.vertex.fromArray(this.restNormals, index).applyQuaternion(this.bend)
				normals[index] = this.vertex.x
				normals[index + 1] = this.vertex.y
				normals[index + 2] = this.vertex.z
			}
		}

		this.positions.needsUpdate = true
		this.normals.needsUpdate = true
	}
}
